"use client";

import { useAssets } from "@/hooks/use-assets";
import { toast } from "@/lib/instrumented-toast";
import { RefreshCwIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { SpinnerInfinity } from "spinners-react/lib/esm/SpinnerInfinity";

import { AssetsDataTable } from "./assets-table";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

export function VersionsView() {
  const assetsQuery = useAssets();
  const assets = useMemo(() => assetsQuery.data?.assets ?? [], [assetsQuery.data?.assets]);
  const lastUpdated = assetsQuery.data?.lastUpdated ?? null;

  const [searchQuery, setSearchQuery] = useState("");
  const [searchParams] = useSearchParams();
  const [filteredCount, setFilteredCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [clientLastFetched, setClientLastFetched] = useState<Date>(new Date());

  const selectedVersion = searchParams.get("version");

  const filteredAssets = useMemo(() => {
    return selectedVersion
      ? (assets || []).filter((asset) => {
          return asset.display_release_month === selectedVersion;
        })
      : assets || [];
  }, [assets, selectedVersion]);

  const handleFilteredCountChange = (filtered: number, total: number) => {
    setFilteredCount(filtered);
    setTotalCount(total);
  };

  const handleSync = () => {
    toast.promise(
      assetsQuery.refetch().then(() => {
        setClientLastFetched(new Date());
      }),
      {
        loading: "Syncing assets...",
        success: "Assets synced",
        error: "Failed to sync assets",
      },
    );
  };

  const isDataOutdated = () => {
    if (!lastUpdated || !clientLastFetched) return false;
    return lastUpdated > clientLastFetched;
  };

  const formatLastUpdated = (date: Date | null) => {
    if (!date) {
      return "Unknown";
    }
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });
  };

  if (assetsQuery.isLoading) {
    return (
      <div className="w-full">
        <div className="flex h-64 items-center justify-center">
          <SpinnerInfinity
            size={70}
            thickness={180}
            speed={149}
            color="rgba(78, 172, 57, 1)"
            secondaryColor="rgba(0, 0, 0, 0.1)"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col gap-4">
      {/* Header with title and search/actions in opposite corners */}
      <div className="flex items-center justify-between">
        {selectedVersion && (
          <h2 className="text-2xl font-semibold">
            Version {selectedVersion}
            <span className="ml-3 text-sm font-normal text-muted-foreground">
              ({filteredCount}/{totalCount} {totalCount === 1 ? "asset" : "assets"})
            </span>
          </h2>
        )}
        {!selectedVersion && (
          <h2 className="text-2xl font-semibold">
            All Versions
            <span className="ml-3 text-sm font-normal text-muted-foreground">
              ({filteredCount}/{totalCount} {totalCount === 1 ? "asset" : "assets"})
            </span>
          </h2>
        )}

        <div className="flex items-center gap-2">
          <Input
            placeholder="Search assets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-64"
          />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleSync}
                    disabled={!isDataOutdated() || assetsQuery.isFetching}
                  >
                    <RefreshCwIcon className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Last synced: {formatLastUpdated(lastUpdated)}</p>
                {isDataOutdated() ? (
                  <p className="text-yellow-500">New data available - click to sync</p>
                ) : (
                  <p className="text-foreground">Your data is already up to date</p>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Table View */}
      <div className="flex-1 overflow-hidden">
        <AssetsDataTable
          searchQuery={searchQuery}
          selectedGroup={null}
          selectedSubgroup={null}
          filteredAssets={filteredAssets}
          onFilteredCountChange={handleFilteredCountChange}
        />
      </div>
    </div>
  );
}
